// When an emergency stops ringing, and how long an alarm has been quiet.
//
// MISSED (2026-10): a critical alarm now RINGS for `SOS_RING_WINDOW_MS` (two minutes, shared with the
// broker through `@libs/realtime`). Once nobody has answered it in that window it is MISSED, like a
// call nobody picked up: it leaves the emergency card, no longer offers Accept, and moves to the
// missed list (the corner pill on a dashboard, the bell drawer everywhere), where Call back and
// Dismiss live. The broker stamps `missedAt` on the stored alert at the same deadline, so every
// console agrees and the log can still say "missed" after someone handles it.
//
// The rule below this paragraph still stands, with "shown" now meaning "on the card OR in the
// missed list": a missed alert is still OPEN, still in the bell, the log and on the map, and the
// missed list stays on screen until each one is called back or dismissed.
//
// An operator opened the console and was shown a full emergency card for an SOS raised TWO DAYS
// earlier, still asking Accept or Decline. That is not an emergency, it is a stuck notification —
// and a surface that cries wolf about a two-day-old alarm is one an operator learns to dismiss,
// which costs them the real alarm underneath it.
//
// AGE DECIDES HOW AN EMERGENCY IS SHOWN, NEVER WHETHER IT IS SHOWN, and that is the correction that
// matters. The first version of this refused to queue an alarm older than the window, on the stated
// grounds that "a helmet re-reports an alarm while the condition persists, so anything still
// happening stays fresh". THAT PREMISE IS FALSE, and an adversarial review of the alarm path is
// what established it:
//
//   - The vendor protocol (`docs/smart_helmet/01-protocol-reference.md` §4) documents ONE device
//     frame plus ONE server echo per alarm. No retransmission is described anywhere in it.
//   - The only re-alarm knobs that exist are `offhelmet_time_interval` and `nomove_time_interval`
//     (`libs/realtime/src/helmet/config.ts`), and we set BOTH to 45 minutes. There is no SOS
//     re-alarm field at all.
//   - So of the eight `critical` alarm types that can raise an emergency, seven have no mechanism
//     that could ever produce a second sighting. `sos_manual` — a physical button press — is one
//     frame, once.
//   - The claim in `03-integration-architecture.md:333` and `helmet/alarms.ts` that "alarms re-fire
//     until acknowledged" carries no vendor citation, and `07-firmware-verification.md:200-203`
//     already flags the closely-related retry assumption as read off our own code rather than
//     observed.
//
// Under the old rule a lone worker who pressed SOS and then could not press it again — unconscious,
// pinned, overcome — had their emergency silently withdrawn five minutes later, including from the
// backlog replay that exists precisely so an alarm raised while the operator was on another page
// still reaches them. The rule spared only a wearer well enough to keep pressing the button.
//
// So nothing is ever removed by a clock. A timed-out emergency stays queued, stays on screen and
// stays answerable; what it loses is the repeating tone, the OS notification and the right to head
// the queue. It also stays open in the store, the bell, the inbox and on the map.
//
// TWO DIFFERENT CLOCKS, deliberately, because they answer different questions:
//
//   - The QUEUE times out on how long an emergency has been ON OFFER to this operator
//     (`offeredAt`, stamped locally in `sos-alert-queue.ts`). Both operands come from the same
//     machine, so browser-vs-broker skew cancels exactly.
//   - The helpers BELOW measure how long the alarm itself has been quiet, from timestamps the
//     BROKER stamped (`real-persistence.ts`). That is the honest thing to show in a log — "no
//     longer reporting" — and it is never used to withdraw anything.

import { SOS_RING_WINDOW_MS } from '@libs/realtime'

import type { RobotAlertItem } from './robot-alerts-store'

export { SOS_RING_WINDOW_MS }

/**
 * How long an emergency keeps demanding an answer.
 *
 * Five minutes is the operator's own figure. It is safe to make it this short ONLY because timing
 * out no longer removes anything: the emergency stays on screen and answerable, so the cost of
 * being early is a quieter card rather than a missed alarm.
 */
export const SOS_TIMEOUT_MS = 5 * 60 * 1000

/**
 * When this alarm was last heard from, in epoch ms, or `NaN` when it carries no usable timestamp.
 *
 * `lastSeenAt` before `recordedAt` because a presence-deduplicated alarm keeps its original
 * `recordedAt` while `lastSeenAt` advances on a repeat — and for "when did we last hear about
 * this?" the last sighting is the only honest answer. Same rule the queue uses to decide which of
 * two alarms for one device is current: two different answers to "how recent is this" inside one
 * feature is how a surface starts contradicting itself.
 *
 * Both fields are stamped by the BROKER (`real-persistence.ts`), not by the device, so this is our
 * own record of when we last heard — not a claim about the helmet's clock.
 */
export function sosRecencyOf(item: RobotAlertItem): number {
  return Date.parse(item.lastSeenAt ?? item.recordedAt ?? '')
}

/**
 * Whether nothing has reported this alarm for `windowMs`.
 *
 * A statement about the ALARM, used to LABEL it — never to withdraw it. The queue's own deadline is
 * a different clock (see the header).
 *
 * FAILS SAFE in both directions that matter:
 *
 *  - An alarm with NO usable timestamp is never called quiet. It cannot be aged, and the
 *    conservative reading of "I don't know when this happened" on an emergency surface is to say
 *    nothing rather than to imply it is over.
 *  - An alarm stamped in the FUTURE is never called quiet either. Both stamps come from the broker
 *    rather than the device, so this is browser-vs-broker skew rather than device drift — smaller,
 *    but real, and a workstation whose clock runs slow must not label live alarms as quiet.
 *    `now - recency` is negative there, which is less than any positive window, so it falls out of
 *    the comparison rather than needing a special case — but it is the reason the comparison is
 *    written this way round and must not be "rearranged" into an absolute difference.
 */
export function sosIsTimedOut(item: RobotAlertItem, now: number, windowMs: number = SOS_TIMEOUT_MS): boolean {
  const recency = sosRecencyOf(item)

  if (!Number.isFinite(recency)) return false

  return now - recency >= windowMs
}

/** How long this alarm has left before it stops asking, or null when it never will. */
export function sosTimeoutRemainingMs(
  item: RobotAlertItem,
  now: number,
  windowMs: number = SOS_TIMEOUT_MS,
): number | null {
  const recency = sosRecencyOf(item)

  if (!Number.isFinite(recency)) return null

  return Math.max(0, recency + windowMs - now)
}

/**
 * Whether a queued emergency has stopped ringing on this screen. ANY of three answers is enough:
 *
 *  - the server already stamped it missed (`missedAt`) — the broker's word, shared by every console;
 *  - the alarm itself has been quiet for the ring window, from broker stamps (what makes a backlog
 *    alarm arrive missed instead of ringing for two minutes on every page load);
 *  - it has been on offer to THIS operator for the ring window, from this machine's clock alone (what
 *    stops it ringing when the workstation's clock runs behind the broker's).
 */
export function sosIsMissed(item: RobotAlertItem, offeredAt: number, now: number): boolean {
  if (item.missedAt) return true

  return now - offeredAt >= SOS_RING_WINDOW_MS || sosIsTimedOut(item, now, SOS_RING_WINDOW_MS)
}

/** When a queued emergency stops ringing, in epoch ms — the earlier of its two clocks. */
export function sosMissedDeadline(item: RobotAlertItem, offeredAt: number): number {
  const recency = sosRecencyOf(item)
  const byAlarm = Number.isFinite(recency) ? recency + SOS_RING_WINDOW_MS : Number.POSITIVE_INFINITY

  return Math.min(offeredAt + SOS_RING_WINDOW_MS, byAlarm)
}

/**
 * Whether an alert belongs in a missed list: an OPEN critical alarm that rang out unanswered.
 *
 * For a surface with no queue of its own (the bell drawer), so only the two shared clocks — the
 * server's stamp and the alarm's own age. Never true for a handled alert: missed is about something
 * still waiting for someone.
 */
export function sosAlertIsMissed(item: RobotAlertItem, now: number): boolean {
  if (item.severity !== 'critical' || item.status !== 'open') return false

  return Boolean(item.missedAt) || sosIsTimedOut(item, now, SOS_RING_WINDOW_MS)
}
